<template>
  <GmapMap
    v-if="isLoadingMap === false && isLoadingGlobal === false"
    id="map"
    ref="mapRef"
    map-type-id="roadmap"
    class="mc-map"
    :zoom="zoom"
    :center="center"
    :options="mapOptions"
    :style="{ minHeight: 'calc(100vh - 118px - 15px)', flexGrow: 1, width: '100%' }"
  >
    <!-- Vehicles -->
    <GmapCluster averageCenter :zoom-on-click="true">
      <VehicleMarker
        v-for="id in Object.keys(vehicles)"
        :id="id"
        :key="`marker-${id}`"
        :vehicle="vehicles[id]"
        :active="selectedVehicleId === id"
        @click="handleClickVehicleIcon(id)"
      />
    </GmapCluster>

    <!-- Points -->
    <PointMarker
      v-for="(point, index) in localPoints"
      :id="point.id"
      :key="`point-marker-${index}`"
      :number="index + 1"
      :marker="{ lat: point.lat, lng: point.lng }"
      @click="handleClickPointMarker(index)"
    />

    <!-- Selected Point -->
    <PointPanel
      v-if="['add-point', 'remove-point'].includes(activePanel)"
      :hasCreate="selectedPointIdx === null"
      :hasDelete="selectedPointIdx !== null"
      :point="selectedPoint"
      @click-create="handleAddPoint"
      @click-delete="handleRemovePoint"
      @close="handleClosePanel"
    />

    <!-- Selected Vehicle -->
    <VehiclePanel v-if="isSelectVehicle" :vehicle="selectedVehicle" />

    <!-- Route -->
    <GmapPolyline
      v-if="isShowRoute && !isLoadingRoute"
      :path="polyline"
      :options="{
        strokeColor: '#3D69A7',
        strokeOpacity: '1.0',
        strokeWeight: '3',
      }"
    />
  </GmapMap>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { gmapApi } from 'vue2-google-maps'
import { MixPanel } from '@/mixin/mixpanel'
import { Helper } from '@/mixin/helper'

import { GET_VEHICLES } from '@/services/api/fleetTaskInstant'
import { GetAddressData } from '@/services/api/map'
import { generateRoute } from '@/services/rest-api-vsms/lib/route/route'
import { GET_PAYLOAD } from '@/services/api/auth'

import VehicleMarker from '../components/VehicleMarker'
import PointMarker from '../components/PointMarker'
import PointPanel from '../components/PointPanel'
import VehiclePanel from '../components/VehiclePanel'

import vehicleMappings from '../lib/constants/vehicle-mappings.js'
import { GOOGLE_MAP_ADDRESS_TYPE } from '../lib/constants/types'
import setMapOptions from '../lib/constants/map-options'
import { EventBus } from '../lib/event-bus.js'

import cloneDeep from 'lodash/cloneDeep'

export default {
  components: {
    VehicleMarker,
    PointMarker,
    PointPanel,
    VehiclePanel,
  },
  mixins: [MixPanel, Helper],
  data() {
    return {
      isLoadingMap: true,
      forceDisconnect: false,
      isLoadingRoute: false,

      vehicles: {},
      mapOptions: {},
      selectedPoint: {},
      selectedVehicle: {},
      center: { lat: -2.958881, lng: 109.5543666 },

      polyline: [],
      localPoints: [],

      zoom: 5,
      selectedPointIdx: null,
      selectedVehicleId: null,

      activePanel: null,
      isSelectPoint: false,
      isSelectVehicle: false,

      vehicleMappings,
      wsPayload: null,
      isUserGroupInputChange: false,
    }
  },
  computed: {
    google: gmapApi,
    ...mapState('fleetTaskInstant', [
      'map',
      'isChangingPoint',
      'selectedPoints',
      'isLoadingGlobal',
      'isStreetViewMode',
      'formValues',
      'listVehicle',
      'userGroupFullObject',
    ]),
    ...mapGetters({
      pointMarkers: 'fleetTaskInstant/pointMarkers',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasUserGroupFeature() {
      return this.hasAccessAll(['tms-user-group:read'])
    },
    isShowRoute() {
      return this.pointMarkers.length >= 2
    },
  },
  watch: {
    isLoadingGlobal: {
      immediate: true,
      async handler(newVal) {
        if (!newVal) {
          if (this.hasUserGroupFeature && this.isSuperUser()) {
            await this.initMap()
          } else {
            await this.getVehiclesData()
            await this.initMap()
            await this.getEncrypDataWsGps()
            await this.startWebSocket()
          }
        }
      },
    },
    selectedPoints(newValue) {
      // prevent set center if add empty point
      const totalPoint = newValue.length
      if (totalPoint === 1 && newValue[0].id === undefined) return

      if (newValue.length === 1) {
        this.zoom = 15
        this.center = { lat: newValue[0].lat, lng: newValue[0].lng }
      }
    },
    async pointMarkers(newValue) {
      await this.$nextTick()
      // always deep clone the selectedPoints from vuex to local state
      this.localPoints = cloneDeep(newValue)

      if (newValue.length < 2) return

      await this.$nextTick()
      // get route data from OSRM
      this.getRouteData(newValue)
    },
    'formValues.user_group_id': function (newValue, oldValue) {
      this.isUserGroupInputChange = newValue !== oldValue
    },
    async listVehicle() {
      if (this.hasUserGroupFeature) {
        this.changeUserGroup()
      }
    },
  },
  created() {
    if (this.$socket !== undefined) {
      this.$socket.close()
    }

    // deep clone the selectedPoints from vuex to local state
    this.localPoints = cloneDeep(this.pointMarkers)

    EventBus.$on('reset-vehicles-on-map', this.clearVehiclesOnUpdate)
  },
  beforeDestroy() {
    EventBus.$off('reset-vehicles-on-map', this.clearVehiclesOnUpdate)

    if (this.$socket !== undefined) {
      this.forceDisconnect = true
      this.$socket.close()
    }
  },
  methods: {
    async getVehiclesData() {
      let vGroupIds = null

      if (
        this.hasUserGroupFeature &&
        this.isSuperUser() &&
        this.userGroupFullObject != null &&
        Array.isArray(this.userGroupFullObject?.vgroups) &&
        this.userGroupFullObject?.vgroups.length > 0
      ) {
        vGroupIds = this.userGroupFullObject.vgroups.map((vg) => Number(vg.id))
      }

      const vehiclesData = await this.$apollo.query({
        client: 'clientLowPrio',
        query: GET_VEHICLES,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            isSharedVehicle: false,
            vgroupId: vGroupIds ?? undefined,
          },
        },
      })

      if (!vehiclesData.data.vehicleStatuses.vehicleStatuses) return

      vehiclesData.data.vehicleStatuses.vehicleStatuses.forEach((vehicle) => {
        // Set data from API to vehicles state
        this.$set(this.vehicles, vehicle.vehicleId, {
          id: vehicle.vehicleId,
          latitude: vehicle.latitude,
          longitude: vehicle.longitude,
          licensePlate: vehicle.licensePlate,
          direction: vehicle.direction,
          calculatedMotionStatus: vehicle.calculatedMotionStatus,
          icon: vehicle.vehicle.vehicleType.icon,
          status:
            this.listVehicle?.[vehicle.vehicleId]?.fleet_task == null
              ? 'available'
              : 'not_available',
        })
      })

      this.isLoadingMap = false
      await this.$nextTick()
    },

    async getRouteData(points) {
      this.isLoadingRoute = true

      const latLng = points.map((point) => `${point.lng},${point.lat}`).join(';')
      const res = await generateRoute(latLng)

      if (!res.data) return

      this.polyline = res.data.routes[0].geometry.coordinates.map(([lng, lat]) => {
        return {
          lat,
          lng,
        }
      })

      await this.$nextTick()
      this.isLoadingRoute = false

      // fit map bounds based on route
      this.fitBounds(this.polyline)
    },

    setCenter({ lat, lng, zoom = 21 }) {
      this.center = { lat, lng }
      this.zoom = zoom
    },

    fitBounds(locations) {
      let bounds = new this.google.maps.LatLngBounds()
      locations.forEach((location) => {
        bounds.extend(new this.google.maps.LatLng(location.lat, location.lng))
      })
      this.map.fitBounds(bounds)
    },

    async initMap() {
      if (this.hasUserGroupFeature && this.isSuperUser()) {
        this.isLoadingMap = false
        await this.$nextTick()
      }

      if (this.map !== null) return

      const self = this
      const mapRef = this.$refs.mapRef

      await mapRef.$mapPromise.then((map) => {
        self.$store.dispatch('fleetTaskInstant/setMap', map)
        self.mapOptions = setMapOptions(self.google)

        const streetView = map.getStreetView()
        streetView.addListener('visible_changed', () => {
          self.$store.dispatch('fleetTaskInstant/setIsStreetViewMode', streetView.getVisible())
        })

        map.addListener('click', async (event) => {
          event.stop()

          if (this.activePanel == 'remove-point' && !this.isSelectPoint) {
            this.isSelectPoint = true
            return
          }

          if (this.isSelectPoint) {
            this.isSelectPoint = false
            this.activePanel = null
            this.selectedPointIdx = null

            return
          }

          const lat = event.latLng.lat()
          const lon = event.latLng.lng()

          const address = await GetAddressData(lat, lon)

          this.selectedPoint = {
            id: Date.now().toString(),
            name: address?.name ?? '', // TODO: enhancement for name location using value inside props address
            fullName: address.display_name,
            lat: address.lat,
            lng: address.lon,
          }

          this.isSelectPoint = true
          this.activePanel = 'add-point'
        })
      })

      this.track('Map Loaded on Create Quick Assignment')
      await this.$nextTick()

      // center to the selected point from liveview
      if (this.selectedPoints.length === 1 && this.selectedPoints[0].id) {
        const point = this.selectedPoints[0]
        this.setCenter({ lat: point.lat, lng: point.lng, zoom: 15 })

        return
      }

      // define array of object lat lng from vehicles
      if (!(this.hasUserGroupFeature && this.isSuperUser())) {
        const locations = []
        for (const [_, vehicle] of Object.entries(this.vehicles)) {
          if (vehicle.latitude || vehicle.longitude) {
            locations.push({
              lat: vehicle.latitude,
              lng: vehicle.longitude,
            })
          }
        }

        // fit bounds from all locations
        await this.$nextTick()
        this.fitBounds(locations)
      }
    },

    async getEncrypDataWsGps() {
      if (!this.hasUserGroupFeature) {
        this.wsPayload = localStorage.getItem('payload')
        return
      }

      const vehicleIds = Object.keys(this.listVehicle).map((id) => Number(id))

      if (!vehicleIds || !vehicleIds.length) {
        this.wsPayload = null
        return
      }

      try {
        const res = await this.$apollo.query({
          query: GET_PAYLOAD,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              gpsVehicleIds: vehicleIds,
              filterByType: ['GPS'],
            },
          },
        })
        this.wsPayload = res.data.getPayload.payload.payload
      } catch (error) {
        this.wsPayload = null
        console.error(error)
      }
    },

    async startWebSocket() {
      if (!this.wsPayload) return

      const self = this
      const obj = {
        type: 'update_tags',
        data: this.wsPayload,
      }

      // Connect web socket
      await this.$store.dispatch(
        'ws/WSConnect',
        `${process.env.VUE_APP_WS_URL}/ws?token=${localStorage.getItem('wsToken')}`
      )
      this.$socket.onopen = () => {
        this.$socket.sendObj(obj)
      }

      this.$socket.onmessage = (data) => {
        const signal = JSON.parse(data.data)

        // Only process data of signal type is gps
        if (!signal.type?.startsWith('gps-')) return

        const payload = signal.payload
        const vehicle = self.vehicles[payload.vehicleId]

        if (!vehicle) return

        const newVehicle = {
          ...vehicle,
          latitude: payload.latitude,
          longitude: payload.longitude,
          direction: payload.direction,
          calculatedMotionStatus: payload.calculatedMotionStatus,
        }

        self.$set(self.vehicles, payload.vehicleId, newVehicle)
      }

      this.$socket.onclose = async () => {
        if (this.forceDisconnect) return
        this.startWebSocket()
      }
    },

    handleClickPointMarker(index) {
      if (this.isSelectPoint) {
        this.activePanel = null
        this.selectedPointIdx = null
        this.isSelectPoint = false
        this.selectedPoint = {}

        return
      }

      this.activePanel = 'remove-point'
      this.selectedPointIdx = index
      this.selectedPoint = cloneDeep(this.localPoints[index])
    },

    async handleAddPoint() {
      const lastPointIdx = this.selectedPoints.length - 1
      const lastPoint = this.selectedPoints[lastPointIdx]
      const newPoint = {
        ...this.selectedPoint,
        type: GOOGLE_MAP_ADDRESS_TYPE,
      }

      if (lastPoint && (lastPoint.id === undefined || lastPoint.id === null)) {
        this.$store.dispatch('fleetTaskInstant/setSelectedPoints', {
          index: lastPointIdx,
          value: newPoint,
        })
      } else {
        this.$store.dispatch('fleetTaskInstant/addSelectedPoints', newPoint)
      }

      await this.$nextTick()

      this.selectedPoint = {}
      this.isSelectPoint = false
      this.activePanel = null
    },

    async handleRemovePoint() {
      const index = this.selectedPointIdx
      this.activePanel = null
      this.selectedPointIdx = null
      await this.$nextTick()

      if (this.selectedPoints.length === 1) {
        this.$store.dispatch('fleetTaskInstant/resetSelectedPoints')
        if (this.hasUserGroupFeature && this.isSuperUser()) {
          this.clearVehiclesOnUpdate()
          await this.$nextTick()
        }
      } else {
        this.$store.dispatch('fleetTaskInstant/removeSelectedPoints', { index })
      }

      this.isSelectPoint = false

      // refetch nearest vehicle if first point removed
      if (index === 0 && (!this.hasUserGroupFeature || this.formValues.user_group_id !== null)) {
        EventBus.$emit('get-nearest-vehicle')
      }
    },

    handleClickVehicleIcon(id) {
      if (this.isSelectVehicle) {
        this.selectedVehicleId = null
        this.isSelectVehicle = false
        this.selectedVehicle = {}
        return
      }

      this.selectedVehicleId = id
      this.selectedVehicle = cloneDeep(this.vehicles[id])

      this.isSelectVehicle = true
      this.setCenter({ lat: this.selectedVehicle.latitude, lng: this.selectedVehicle.longitude })
    },

    handleClosePanel() {
      this.activePanel = null
    },

    async changeUserGroup() {
      if (this.isUserGroupInputChange) {
        this.isUserGroupInputChange = false
        this.clearVehiclesOnUpdate()
        await this.getVehiclesData()
        await this.getEncrypDataWsGps()
        await this.startWebSocket()

        // Fit bounds to show all vehicles
        const locations = []
        for (const [_, vehicle] of Object.entries(this.vehicles)) {
          if (vehicle.latitude || vehicle.longitude) {
            locations.push({
              lat: vehicle.latitude,
              lng: vehicle.longitude,
            })
          }
        }

        if (locations.length > 0) {
          this.fitBounds(locations)
        }
      }
    },

    clearVehiclesOnUpdate() {
      if (this.$socket !== undefined) {
        this.forceDisconnect = true
        this.$socket.close()

        this.$nextTick(() => {
          this.forceDisconnect = false
        })
      }

      this.vehicles = {}
    },
  },
}
</script>
